import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { getCurrentSession, signOut as cognitoSignOut } from './cognito';

type AuthUser = {
  email: string;
  sub: string;
  idToken: string;
  accessToken: string;
};

type AuthState = {
  user: AuthUser | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => void;
};

const AuthCtx = createContext<AuthState | null>(null);

function sessionToUser(session: Awaited<ReturnType<typeof getCurrentSession>>): AuthUser | null {
  if (!session) return null;
  const idPayload = session.getIdToken().decodePayload();
  return {
    email: idPayload.email,
    sub: idPayload.sub,
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
  };
}

// Cross-app hint so the landing page can flip its "Join the Initiative" CTA
// to "User Portal" when a fresh session exists. The landing page reads this
// key from localStorage and considers it valid for 7 days (matching the
// refresh-token TTL).
const SESSION_KEY = 'mk_session';
function writeSessionHint() {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify({ lastLogin: new Date().toISOString() }));
  } catch { /* private mode etc — non-fatal */ }
}
function clearSessionHint() {
  try { localStorage.removeItem(SESSION_KEY); } catch { /* non-fatal */ }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const session = await getCurrentSession();
    const next = sessionToUser(session);
    setUser(next);
    if (next) writeSessionHint(); else clearSessionHint();
  }, []);

  // On boot: try to revive the session. If the refresh token is older
  // than 7 days, getCurrentSession returns null and the user lands on
  // the login page — exactly the "weekly refresh" behaviour we want.
  useEffect(() => {
    (async () => {
      await refresh();
      setLoading(false);
    })();
  }, [refresh]);

  // Re-check periodically so an expired token forces sign-out mid-session.
  useEffect(() => {
    const id = setInterval(refresh, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(id);
  }, [refresh]);

  const doSignOut = useCallback(() => {
    cognitoSignOut();
    clearSessionHint();
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, signOut: doSignOut }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
